import { EventEmitter } from '../../core/Event';
import type { Random } from '../../core/Random';
import type { PickEmEvent } from './PickEmEvents';
import type { PickEmConfig, PickEmPrize } from './PickEmTypes';

/**
 * Authoritative state for a Pick-Em bonus session: the shuffled prize
 * pool, picks remaining, the running list of reveals.
 *
 * <p>Construction shuffles the supplied prize array against the configured
 * {@code boxCount} so positions are random per session. Hosts call
 * {@link pick} for each tap; the model emits {@code box-revealed} (and
 * eventually {@code pick-em-complete}). Already-picked boxes are no-ops.
 */
export class PickEmModel {
  public readonly events = new EventEmitter<PickEmEvent>();
  public readonly boxCount: number;
  public readonly picksAllowed: number;
  public readonly columnCount: number;
  private readonly prizes: PickEmPrize[];
  private readonly revealed = new Set<number>();
  private readonly reveals: { index: number; prize: PickEmPrize }[] = [];
  private picksRemaining: number;

  constructor(config: PickEmConfig, random: Random) {
    this.boxCount = config.boxCount ?? 9;
    this.picksAllowed = config.picksAllowed ?? 3;
    this.picksRemaining = this.picksAllowed;
    this.columnCount = config.columnCount ?? Math.ceil(Math.sqrt(this.boxCount));

    // Pad prize pool with zero-value boxes if short, then shuffle into
    // {@link boxCount} slots.
    const padded: PickEmPrize[] = [...config.prizes];
    while (padded.length < this.boxCount) padded.push({ value: 0 });
    this.prizes = shuffle(padded.slice(0, this.boxCount), random);
  }

  /** True iff the player has picks remaining. */
  isActive(): boolean {
    return this.picksRemaining > 0;
  }

  /** Number of picks left before the game completes. */
  picksLeft(): number {
    return this.picksRemaining;
  }

  /** Prize at {@code index} — only available after the box has been
   * revealed (otherwise null). Hosts use this for replay / state restore. */
  prizeAt(index: number): PickEmPrize | null {
    if (!this.revealed.has(index)) return null;
    return this.prizes[index] ?? null;
  }

  isRevealed(index: number): boolean {
    return this.revealed.has(index);
  }

  totalWinnings(): number {
    return this.reveals.reduce((sum, r) => sum + r.prize.value, 0);
  }

  /** Reveal box {@code index}. No-op if already revealed or the bonus is
   * over. Returns the prize on success, null otherwise. Emits
   * {@code box-revealed}, then {@code pick-em-complete} when the last
   * pick is consumed. */
  pick(index: number): PickEmPrize | null {
    if (!this.isActive()) return null;
    if (index < 0 || index >= this.boxCount) return null;
    if (this.revealed.has(index)) return null;

    const prize = this.prizes[index]!;
    this.revealed.add(index);
    this.reveals.push({ index, prize });
    this.picksRemaining -= 1;
    this.events.emit({
      kind: 'box-revealed',
      index,
      prize,
      picksRemaining: this.picksRemaining,
    });

    if (this.picksRemaining === 0) {
      this.events.emit({
        kind: 'pick-em-complete',
        totalWinnings: this.totalWinnings(),
        reveals: this.reveals.slice(),
      });
    }
    return prize;
  }

  dispose(): void {
    this.events.clear();
  }
}

function shuffle<T>(array: readonly T[], random: Random): T[] {
  const result = array.slice();
  for (let i = result.length - 1; i > 0; i--) {
    const j = random.nextIntBetween(0, i + 1);
    [result[i], result[j]] = [result[j]!, result[i]!];
  }
  return result;
}
